import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import dayjs from 'dayjs';

import { BadgeStatus } from '@shared/components/badge-status/badge-status';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { ApiService, AwsFile } from '@shared/services/api.service';
import { OverlayService } from '@shared/services/overlay.service';

import { MonthStats } from '../period-close.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex items-center rounded-[8px] border bg-white py-[5px]',
  },
  imports: [DatePipe, MoneyPipe, BadgeStatus, ButtonComponent, TooltipDirective],
  selector: 'aux-expense-analytic-card',
  template: `
    <div class="flex gap-y-[4px]">
      <div class="flex flex-col gap-[4px] border-r px-[8px]">
        <div class="h-[20px] text-[16px] font-medium">Month</div>
        @for (month of months(); track month.date) {
          <div class="flex h-[20px] items-center">{{ month.date | date: 'MMM' }}</div>
        }
      </div>
      <div class="flex flex-col gap-[4px] border-r px-[8px]">
        <div class="h-[20px] text-center text-[16px] font-medium">Status</div>
        @for (month of months(); track month.date) {
          <div class="h-[20px]">
            <aux-badge-status
              [label]="month.status"
              [variant]="
                month.status === 'Open'
                  ? 'positive'
                  : month.status === 'Future'
                    ? 'info'
                    : 'default'
              "
            />
          </div>
        }
      </div>
      <div class="flex flex-col gap-[4px] border-r px-[8px]">
        <div class="h-[20px] text-right text-[16px] font-medium">Net Position</div>
        @for (month of months(); track month.date) {
          <div class="flex h-[20px] items-center justify-end">
            {{ month.eom_accruals | money: undefined : false }}
          </div>
        }
      </div>
      <div class="flex flex-col items-center gap-[4px] px-[8px]">
        <div class="h-[20px] text-center text-[16px] font-medium">Audit Package</div>
        @for (month of months(); track month.date; let index = $index) {
          @let isClosed = month.status === 'Closed';
          @let isAuditPackageAvailable = month.auditPackages?.length;
          <aux-button
            class="flex h-[20px] items-center"
            label="Download"
            (clickEmit)="onAuditPackageClick(month)"
            variant="hyperlink"
            classList="p-0 text-aux-blue"
            icon="Download"
            [iconSize]="14"
            [attr.data-pendo-id]="pendoIds()[index]"
            [disabled]="!isClosed || !isAuditPackageAvailable"
            [auxTooltip]="
              isClosed
                ? isAuditPackageAvailable
                  ? ''
                  : 'No Audit Package available.'
                : 'Audit Package available for Closed Months only.'
            "
          />
        }
      </div>
    </div>
  `,
})
export class ExpenseAnalyticCard {
  apiService = inject(ApiService);
  overlayService = inject(OverlayService);

  months = input.required<MonthStats[]>();

  pendoIds = computed(() => {
    return this.months().map((month) => {
      const cardNumber = (dayjs(month.date).month() % 3) + 1;

      return `monthly-audit-package-card-${cardNumber}`;
    });
  });

  onAuditPackageClick(month: MonthStats): void {
    if (month.status === 'Closed' && month.auditPackages) {
      const ref = this.overlayService.loading();

      const files = month.auditPackages;

      files.forEach((file) => {
        let fileName = file.name;

        if (fileName.includes('zip')) {
          fileName = fileName.replace('.zip', '');
        }

        if (fileName.toLowerCase().includes('accrual')) {
          void this.apiService.downloadFile({
            fileName: fileName,
            key: file.bucket_key,
          } as AwsFile);
        } else {
          void this.apiService.downloadZipOrFile(file.bucket_key, fileName);
        }
      });
      ref.close();
    }
  }
}
